import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Award, ChevronDown, Video, Tv, Sliders, Share2, Palette, Code2, Users, ArrowRight, Camera } from 'lucide-react';
import VideoHero from '../components/layout/VideoHero';
import ParallaxStars from '../components/ui/ParallaxStars';
import { projects as projectsTr } from '../data/projects';
import { projectsEn } from '../data/projects.en';
import { referenceLogos } from '../data/clients';
import whatsappIcon from '../assets/WhatsappICON.png';
import we24Logo from '../assets/WE24-Logo - BEYAZ-Crop.png';
import hero1 from '../assets/HERO-1.webp';
import hero2 from '../assets/HERO-2.webp';
import hero3 from '../assets/HERO-3.webp';
import hero4 from '../assets/HERO-4.webp';
import { useLanguage } from '../context/LanguageContext';
import { tr } from '../locales/tr';
import { en } from '../locales/en';

const Home: React.FC = () => {
  const { language } = useLanguage();
  const t = language === 'TR' ? tr.home : en.home;
  const currentProjects = language === 'TR' ? projectsTr : projectsEn;

  // Flagship projects for home page
  const homeProjects = currentProjects.filter(p => p.featured).slice(0, 8);

  const capabilities = [
    {
      title: language === 'TR' ? "Film ve Video Prodüksiyon" : "Film & Video Production",
      description: language === 'TR'
        ? "Reklam, tanıtım ve fabrika filmleri, belgeseller, müzik klipleri ve lisanslı drone çekimleri."
        : "Commercials, corporate & factory overviews, documentaries, music videos, and aerial drone capture.",
      icon: <Video className="w-5 h-5 text-[#ff7a63]" />,
      path: language === 'TR' ? "/hizmetler/video-produksiyon" : "/en/services/video-produksiyon"
    },
    {
      title: language === 'TR' ? "Canlı Yayın ve Reji" : "Live Broadcasting & Streaming",
      description: language === 'TR'
        ? "Çok kameralı 4K reji altyapısı, e-spor finalleri, ürün lansmanları ve eşzamanlı simulcast yayın."
        : "Multi-camera vision mixing, esports tournaments, product keynotes, and live broadcast graphics.",
      icon: <Tv className="w-5 h-5 text-[#ff7a63]" />,
      path: language === 'TR' ? "/hizmetler/canli-yayin-program" : "/en/services/canli-yayin-program"
    },
    {
      title: language === 'TR' ? "Post-Prodüksiyon ve VFX" : "Post-Production & VFX",
      description: language === 'TR'
        ? "DaVinci Resolve renk düzenleme (color grading), görsel efektler (VFX), 3D animasyon ve ses tasarımı."
        : "Editing, DaVinci Resolve color grading, VFX, 3D animation, and multilingual mastering.",
      icon: <Sliders className="w-5 h-5 text-[#ff7a63]" />,
      path: language === 'TR' ? "/hizmetler/post-produksiyon" : "/en/services/post-produksiyon"
    },
    {
      title: language === 'TR' ? "Fotoğraf Prodüksiyonu" : "Photography Production",
      description: language === 'TR'
        ? "Mimari mekânlar, endüstriyel tesisler, e-ticaret stüdyo ürün çekimleri ve moda katalogları."
        : "Architectural, factory manufacturing, commercial e-commerce products, and fashion lookbooks.",
      icon: <Camera className="w-5 h-5 text-[#ff7a63]" />,
      path: language === 'TR' ? "/hizmetler/fotograf-produksiyon" : "/en/services/fotograf-produksiyon"
    },
    {
      title: language === 'TR' ? "Dijital ve Sosyal Medya" : "Digital & Social Media",
      description: language === 'TR'
        ? "Hesap yönetimi, dikey video (Reels/TikTok) üretimi, Meta reklamları ve YouTube video SEO."
        : "Channel management, vertical video production, targeted Meta ads, YouTube growth, and SEO.",
      icon: <Share2 className="w-5 h-5 text-[#ff7a63]" />,
      path: language === 'TR' ? "/hizmetler/dijital-ve-sosyal-medya" : "/en/services/dijital-ve-sosyal-medya"
    },
    {
      title: language === 'TR' ? "Marka ve Grafik Tasarım" : "Brand Identity & Design",
      description: language === 'TR'
        ? "Kurumsal kimlik rehberi, logo tasarımı, dergi ve katalog mizanpajı, ambalaj grafikleri."
        : "Brand identity systems, guidelines, campaign creative, packaging, and editorial publication layouts.",
      icon: <Palette className="w-5 h-5 text-[#ff7a63]" />,
      path: language === 'TR' ? "/hizmetler/tasarim-ve-marka" : "/en/services/tasarim-ve-marka"
    },
    {
      title: language === 'TR' ? "Web ve Yazılım Geliştirme" : "Web & Software Engineering",
      description: language === 'TR'
        ? "Ultra hızlı modern web siteleri, e-ticaret, mobil PWA, SaaS ve özel kurumsal iş yazılımları."
        : "High-speed corporate websites, e-commerce, custom SaaS platforms, and workflow automations.",
      icon: <Code2 className="w-5 h-5 text-[#ff7a63]" />,
      path: language === 'TR' ? "/hizmetler/web-ve-yazilim" : "/en/services/web-ve-yazilim"
    }
  ];

  const metrics = [
    { value: "15+", label: language === 'TR' ? "Yıllık Ekip Deneyimi" : "Years of Experience" },
    { value: "80+", label: language === 'TR' ? "Marka ve İş Ortağı" : "Brand Partners" },
    { value: "400+", label: language === 'TR' ? "Etkinlik Prodüksiyonu" : "Events Broadcasted" },
    { value: "50+", label: language === 'TR' ? "Kurumsal Tanıtım Filmi" : "Corporate Films" },
    { value: "50+", label: language === 'TR' ? "Konser ve Müzik Klibi" : "Concerts & Music Videos" }
  ];

  const processSteps = [
    {
      num: "01",
      title: language === 'TR' ? "Dinliyoruz" : "Listen & Brief",
      desc: language === 'TR'
        ? "Markanızın hedeflerini, vizyonunu ve teknik ihtiyaçlarını anlamak için detaylı brifing alıyoruz."
        : "We conduct deep briefing sessions to align on your brand targets, audience, and technical specs."
    },
    {
      num: "02",
      title: language === 'TR' ? "Stratejiyi Kuruyoruz" : "Formulate Strategy",
      desc: language === 'TR'
        ? "Film prodüksiyonundan yazılıma kadar hedefinize en uygun doğru üretim modelini tasarlıyoruz."
        : "We architect the optimal production model tailored specifically to your objectives."
    },
    {
      num: "03",
      title: language === 'TR' ? "Tasarlıyoruz" : "Design & Concept",
      desc: language === 'TR'
        ? "Görsel dili, arayüzü ve içerik konseptlerini markanıza özgü modern bir çizgide şekillendiriyoruz."
        : "We shape visual narratives, storyboards, and digital UI in a cohesive aesthetic language."
    },
    {
      num: "04",
      title: language === 'TR' ? "Üretiyoruz" : "Execute & Produce",
      desc: language === 'TR'
        ? "Çekimler, kurgu, kodlama ve içerik tasarımı süreçlerini stüdyomuzda yüksek kalitede tamamlıyoruz."
        : "Our in-house team executes filming, editing, color grading, and code engineering."
    },
    {
      num: "05",
      title: language === 'TR' ? "Yayına Alıyor & Büyütüyoruz" : "Launch & Scale",
      desc: language === 'TR'
        ? "Projelerinizi tüm platformlarda yayına alıyor, performansını takip ederek sürekli destek veriyoruz."
        : "We deploy across all platforms, track real-time analytics, and provide continuous optimization."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>
          {language === 'TR'
            ? "WE24 Agency - Dijital İletişim, Film Prodüksiyon & Yazılım"
            : "WE24 Agency - Creative Communication, Film Production & Software"}
        </title>
        <meta
          name="description"
          content={
            language === 'TR'
              ? "Şişli stüdyomuzda sosyal medya yönetimi, kurumsal film prodüksiyonu, çok kameralı canlı yayın rejileri, post-prodüksiyon ve modern web yazılımları üretiyoruz."
              : "Creative communication agency in Istanbul producing high-end video, multi-camera live streaming, post-production, and custom web software."
          }
        />
        <link rel="canonical" href="https://www.we24agency.com/" />
      </Helmet>

      {/* 1. Cinematic Hero Section */}
      <section className="relative h-[90vh] min-h-[580px] flex items-center justify-center overflow-hidden">
        <VideoHero av1Url="/hero-loop-av1.webm" webmUrl="/hero-loop.webm" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center flex flex-col items-center justify-center mt-6">
          <span className="text-xs font-bold tracking-[0.25em] text-[#d5503b] mb-4 uppercase inline-block animate-fade-rise">
            {t.heroTag}
          </span>
          <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight font-display text-white max-w-5xl mb-6 animate-fade-rise-delay">
            {language === 'TR' ? (
              <>
                Fikirleri <em className="text-zinc-400 italic font-normal">görüntüye</em>,<br />
                markaları <em className="text-zinc-400 italic font-normal">etkiye</em> dönüştürüyoruz.
              </>
            ) : (
              <>
                Transforming ideas into <em className="text-zinc-400 italic font-normal">imagery</em>,<br />
                brands into <em className="text-zinc-400 italic font-normal">impact</em>.
              </>
            )}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl leading-relaxed mb-8 animate-fade-rise-delay-2">
            {t.heroSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3.5 animate-fade-rise-delay-2">
            <Link
              to={language === 'TR' ? "/projeler" : "/en/projects"}
              className="px-7 py-3 bg-foreground text-background text-xs sm:text-sm font-semibold rounded-full hover:bg-foreground/90 transition-all duration-300 w-full sm:w-auto text-center shadow-md"
            >
              {t.exploreProjects}
            </Link>
            <Link
              to={language === 'TR' ? "/iletisim" : "/en/contact"}
              className="liquid-glass border border-white/15 px-7 py-3 text-xs sm:text-sm font-semibold rounded-full hover:border-[#d5503b]/40 text-foreground transition-all duration-300 w-full sm:w-auto text-center"
            >
              {t.contactUs}
            </Link>
          </div>
        </div>

        {/* Scroll indicator with Modern Loop Animated Arrow */}
        <button
          onClick={() => {
            const el = document.getElementById('portfolio-section');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }}
          className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 text-zinc-300 hover:text-white flex flex-col items-center gap-1 group cursor-pointer transition-all duration-300"
          aria-label={language === 'TR' ? "Aşağı kaydır" : "Scroll down"}
        >
          <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-zinc-300/80 group-hover:text-white transition-colors">
            {language === 'TR' ? "Kaydır" : "Scroll"}
          </span>
          <div className="w-8 h-8 rounded-full bg-black/40 border border-white/20 backdrop-blur-md flex items-center justify-center group-hover:border-[#d5503b]/60 group-hover:bg-black/60 transition-all shadow-lg">
            <ChevronDown className="w-4 h-4 text-[#d5503b] animate-bounce transition-transform group-hover:translate-y-0.5" />
          </div>
        </button>
      </section>

      {/* 2. Flagship Projects Asymmetric Grid */}
      <section id="portfolio-section" className="py-16 sm:py-20 bg-[#05131f] relative z-10 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] text-[#d5503b] font-semibold mb-1.5 block">
                {language === 'TR' ? "Portfolyo" : "Portfolio"}
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-display tracking-tight text-white">
                {t.featuredProjectsTitle}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground max-w-md mt-1.5 leading-relaxed">
                {t.featuredProjectsSubtitle}
              </p>
            </div>
            <Link
              to={language === 'TR' ? "/projeler" : "/en/projects"}
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground hover:text-white font-medium border-b border-border hover:border-foreground pb-0.5 transition-all"
            >
              {t.viewAllProjects}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Symmetric Aligned Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {homeProjects.map((project) => {
              const projectUrl = language === 'EN' ? `/en/projects/${project.slug}` : `/projeler/${project.slug}`;
              const metaLine = [
                project.categories && project.categories[0] ? project.categories[0] : null,
                project.categories && project.categories[1] ? project.categories[1] : null,
                project.year ? `${project.year}` : null
              ].filter(Boolean).join(' · ');

              return (
                <div
                  key={project.id}
                  className="flex flex-col h-full bg-card/25 border border-white/[0.08] hover:border-[#ff7a63]/40 rounded-3xl p-4 sm:p-5 transition-all duration-300 hover:-translate-y-1.5 group backdrop-blur-md shadow-lg"
                >
                  <Link 
                    to={projectUrl} 
                    className="relative overflow-hidden aspect-video bg-zinc-900 rounded-2xl border border-white/10 mb-4 shrink-0"
                  >
                    <img
                      src={project.coverImage}
                      alt={project.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                      width="960"
                      height="540"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5">
                      <span className="text-xs font-bold uppercase tracking-wider text-white bg-[#ff7a63] px-3.5 py-1.5 rounded-full shadow-lg inline-flex items-center gap-1.5">
                        <span>{language === 'TR' ? "Vaka Analizi" : "View Case Study"}</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>

                  <div className="flex flex-col justify-between flex-grow">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2.5">
                        <span className="text-[11px] font-semibold tracking-wider text-[#ff7a63] uppercase">
                          {metaLine}
                        </span>
                      </div>
                      
                      <Link to={projectUrl}>
                        <h3 className="text-lg sm:text-xl font-display text-white group-hover:text-[#ff7a63] transition-colors leading-snug mb-2 font-medium">
                          {project.title}
                        </h3>
                      </Link>
                      
                      <p className="text-xs sm:text-[13px] text-zinc-300 leading-relaxed line-clamp-2 mb-4 font-normal">
                        {project.strategicImpact || project.shortDescription}
                      </p>
                    </div>

                    <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between text-xs">
                      <span className="text-xs font-medium text-zinc-400">
                        {project.client}
                      </span>
                      <Link
                        to={projectUrl}
                        className="text-xs font-semibold text-zinc-300 group-hover:text-[#ff7a63] inline-flex items-center gap-1 transition-colors"
                      >
                        <span>{language === 'TR' ? "Vakayı İncele" : "Case Study"}</span>
                        <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. Integrated Agency Statement */}
      <section className="py-20 sm:py-24 relative z-10 text-center overflow-hidden border-y border-white/[0.08]">
        {/* Frosted Glass Background Image (High Visibility) */}
        <div className="absolute inset-0 z-0">
          <img
            src={hero2}
            alt="WE24 Agency Cinema & Production"
            className="w-full h-full object-cover object-center filter brightness-[0.78] contrast-105 scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05131f]/60 via-[#071927]/45 to-[#05131f]/65 backdrop-blur-[3px]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <div className="inline-flex items-center justify-center p-2.5 rounded-2xl bg-black/40 border border-white/20 backdrop-blur-xl mb-6 shadow-2xl">
            <Award className="w-7 h-7 text-[#d5503b]" />
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-display leading-tight tracking-tight text-white mb-6 drop-shadow-[0_4px_16px_rgba(0,0,0,0.8)]">
            {language === 'TR' ? (
              <>
                “Bir fikri yalnızca anlatmıyoruz.<br />
                Stratejisini kuruyor, görüntüsünü üretiyor,<br />
                dijital deneyimini tasarlıyor ve yayına taşıyoruz.”
              </>
            ) : (
              <>
                “We don’t just convey an idea.<br />
                We engineer its strategy, film its visuals,<br />
                design its digital experience, and broadcast it to the world.”
              </>
            )}
          </h2>
          <div className="bg-black/45 border border-white/20 p-6 sm:p-8 rounded-2xl backdrop-blur-xl max-w-3xl mx-auto shadow-2xl">
            <p className="text-xs sm:text-sm text-zinc-200 leading-relaxed font-normal">
              {language === 'TR'
                ? "WE24 Agency, modern markaların görsel ve dijital dünyadaki tüm ihtiyaçlarını tek çatı altında çözebilen hibrit bir ajans modelidir. Fikir geliştirme, sinematik çekimler, post-prodüksiyon montajları, sosyal medya büyümesi ve karmaşık yazılım altyapılarını tek bir vizyonla birleştiriyoruz."
                : "WE24 Agency is a hybrid agency model solving all visual and digital challenges under one roof. We unify creative concepts, cinema production, post-production mastery, social growth, and robust software architectures into one coherent vision."}
            </p>
          </div>
        </div>
      </section>

      {/* 4. Capabilities Grid (Dark Cinematic Theme with Giant Animated White WE24 Logos Infinite Loop) */}
      <section className="py-20 sm:py-28 bg-gradient-to-b from-[#05131f] via-[#071927] to-[#040f1a] relative z-10 overflow-hidden border-b border-white/[0.08]">
        {/* Top Feather Transition from Statement Section */}
        <div className="absolute top-0 inset-x-0 h-28 bg-gradient-to-b from-[#05131f] to-transparent pointer-events-none z-[2]" />

        {/* Animated Background: Giant Sliding White WE24 Logos in Pure Infinite Loop */}
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden flex flex-col justify-center gap-10 sm:gap-16 select-none">
          {/* Marquee Row 1 - Sliding Left (Seamless Infinite Track) */}
          <div className="flex w-max animate-marquee-slow">
            {/* Track 1 */}
            <div className="flex items-center gap-48 sm:gap-72 lg:gap-96 pr-48 sm:pr-72 lg:pr-96 shrink-0">
              {[...Array(6)].map((_, i) => (
                <img
                  key={`logo-track1-a-${i}`}
                  src={we24Logo}
                  alt="WE24"
                  className="h-32 sm:h-48 lg:h-64 w-auto object-contain opacity-[0.08] filter drop-shadow-[0_0_30px_rgba(255,255,255,0.06)]"
                  loading="lazy"
                />
              ))}
            </div>
            {/* Track 2 (Duplicate for Seamless Loop) */}
            <div className="flex items-center gap-48 sm:gap-72 lg:gap-96 pr-48 sm:pr-72 lg:pr-96 shrink-0" aria-hidden="true">
              {[...Array(6)].map((_, i) => (
                <img
                  key={`logo-track1-b-${i}`}
                  src={we24Logo}
                  alt="WE24"
                  className="h-32 sm:h-48 lg:h-64 w-auto object-contain opacity-[0.08] filter drop-shadow-[0_0_30px_rgba(255,255,255,0.06)]"
                  loading="lazy"
                />
              ))}
            </div>
          </div>

          {/* Marquee Row 2 - Sliding Right (Seamless Infinite Track) */}
          <div className="flex w-max animate-marquee-reverse-slow">
            {/* Track 1 */}
            <div className="flex items-center gap-48 sm:gap-72 lg:gap-96 pr-48 sm:pr-72 lg:pr-96 shrink-0">
              {[...Array(6)].map((_, i) => (
                <img
                  key={`logo-track2-a-${i}`}
                  src={we24Logo}
                  alt="WE24"
                  className="h-32 sm:h-48 lg:h-64 w-auto object-contain opacity-[0.08] filter drop-shadow-[0_0_30px_rgba(255,255,255,0.06)]"
                  loading="lazy"
                />
              ))}
            </div>
            {/* Track 2 (Duplicate for Seamless Loop) */}
            <div className="flex items-center gap-48 sm:gap-72 lg:gap-96 pr-48 sm:pr-72 lg:pr-96 shrink-0" aria-hidden="true">
              {[...Array(6)].map((_, i) => (
                <img
                  key={`logo-track2-b-${i}`}
                  src={we24Logo}
                  alt="WE24"
                  className="h-32 sm:h-48 lg:h-64 w-auto object-contain opacity-[0.08] filter drop-shadow-[0_0_30px_rgba(255,255,255,0.06)]"
                  loading="lazy"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Foreground Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-xs uppercase tracking-[0.25em] text-[#d5503b] font-bold mb-2 block">
              {language === 'TR' ? "Yetenekler" : "Capabilities"}
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display tracking-tight text-white drop-shadow-[0_4px_16px_rgba(0,0,0,0.8)]">
              {t.capabilitiesTitle}
            </h2>
            <p className="text-xs sm:text-sm text-zinc-300 max-w-lg mx-auto mt-2 leading-relaxed">
              {t.capabilitiesSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {capabilities.map((cap) => (
              <div
                key={cap.title}
                className="bg-[#081b2a]/65 border border-white/15 p-5 sm:p-6 rounded-2xl flex flex-col justify-between hover:border-[#d5503b]/60 hover:bg-[#071927]/90 backdrop-blur-2xl transition-all duration-300 group hover:-translate-y-1 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
              >
                <div className="flex flex-col gap-3">
                  <div className="w-11 h-11 bg-white/[0.06] rounded-xl flex items-center justify-center border border-white/10 text-white group-hover:bg-[#d5503b] group-hover:border-[#d5503b] transition-all duration-300 shadow-md shrink-0">
                    {cap.icon}
                  </div>
                  <div className="flex flex-col gap-1">
                    <h3 className="text-base sm:text-lg font-bold tracking-tight text-white group-hover:text-zinc-100 transition-colors">{cap.title}</h3>
                    <p className="text-xs text-zinc-300 leading-relaxed font-normal">{cap.description}</p>
                  </div>
                </div>
                <div className="mt-5 pt-3.5 border-t border-white/10 flex items-center justify-between">
                  <Link
                    to={cap.path}
                    className="text-[11px] uppercase tracking-wider font-semibold text-zinc-300 group-hover:text-[#d5503b] inline-flex items-center gap-1.5 transition-colors"
                  >
                    {language === 'TR' ? "Detayları Gör" : "Learn More"}
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Metrics with HERO-3 Frosted Glass */}
      <section className="py-16 sm:py-20 relative z-10 overflow-hidden border-y border-white/[0.08]">
        {/* Frosted Glass Background Image (High Visibility) */}
        <div className="absolute inset-0 z-0">
          <img
            src={hero3}
            alt="WE24 Agency Metrics & Experience"
            className="w-full h-full object-cover object-center filter brightness-[0.78] contrast-105 scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#071927]/65 via-black/45 to-[#071927]/65 backdrop-blur-[3px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 sm:gap-6 text-center">
            {metrics.map((m) => (
              <div
                key={m.label}
                className="flex flex-col gap-1.5 p-5 rounded-2xl bg-black/40 border border-white/20 backdrop-blur-xl hover:border-[#d5503b]/60 transition-all hover:scale-105 shadow-2xl"
              >
                <span className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">{m.value}</span>
                <span className="text-[11px] sm:text-xs text-zinc-200 uppercase tracking-wider font-semibold">{m.label}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] sm:text-[11px] text-zinc-300 text-center mt-6 max-w-xl mx-auto leading-normal font-medium drop-shadow-sm">
            {language === 'TR'
              ? "* Paylaşılan istatistikler kurucu ekibimizin 15 yılı aşan kolektif sektörel üretim ve operasyon geçmişini yansıtmaktadır."
              : "* Metrics reflect over 15 years of cumulative production and broadcasting experience by our core team."}
          </p>
        </div>
      </section>

      {/* 6. Brands & Reference Logos (Referanslar with Parallax Stars Background) */}
      <section className="py-16 sm:py-24 bg-[#030811] border-b border-white/[0.08] relative z-10 overflow-hidden">
        {/* Parallax Stars Background */}
        <ParallaxStars density="normal" speed={0.8} opacity={0.7} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 mb-12 text-center">
          <span className="text-xs uppercase tracking-[0.25em] text-[#d5503b] font-bold block mb-2">
            {language === 'TR' ? "Referanslar" : "Client Roster"}
          </span>
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-display text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]">
            {language === 'TR' ? "Birlikte Ürettiğimiz Markalar" : "Brands We Have Created With"}
          </h3>
          <p className="text-xs sm:text-sm md:text-base text-zinc-300 max-w-2xl mx-auto mt-3 leading-relaxed">
            {language === 'TR'
              ? "Bugüne kadar sinematik prodüksiyon, strateji ve dijital deneyim ürettiğimiz değerli iş ortaklarımız."
              : "Valued brand partners for whom we have produced cinematic production, strategy, and digital experiences."}
          </p>
        </div>

        {/* Direct Centered Logos & Typography Grid (%80 Base Opacity + %50 White Overlay Layer) */}
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3.5 sm:gap-4 items-stretch justify-center">
            {referenceLogos.map((item) => (
              <div
                key={item.name}
                className="h-20 sm:h-24 bg-white/[0.04] border border-white/10 hover:border-[#d5503b]/60 hover:bg-white/[0.08] backdrop-blur-md rounded-2xl p-3 sm:p-4 flex flex-col items-center justify-center text-center transition-all duration-300 group shadow-md hover:-translate-y-1 hover:shadow-xl cursor-default relative overflow-hidden"
                title={item.name}
              >
                {item.isTypography ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center px-1 opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300">
                    <span className="font-display tracking-tight text-white font-bold text-xs sm:text-sm uppercase leading-tight text-center drop-shadow-sm">
                      {item.name}
                    </span>
                    {item.subtitle && (
                      <span className="text-[8px] uppercase tracking-wider text-[#d5503b] font-semibold mt-0.5 text-center">
                        {item.subtitle}
                      </span>
                    )}
                  </div>
                ) : item.logo ? (
                  <div className="relative w-full h-full flex items-center justify-center text-center">
                    {/* 1. Base Logo with Original Colors at 80% Opacity */}
                    <img
                      src={item.logo}
                      alt={item.name}
                      className="max-h-10 sm:max-h-11 max-w-[80%] w-auto h-auto object-contain opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300 mx-auto my-auto block pointer-events-none"
                      loading="lazy"
                    />
                    {/* 2. Top 50% Semi-Transparent White Overlay Layer */}
                    <img
                      src={item.logo}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 max-h-10 sm:max-h-11 max-w-[80%] w-auto h-auto object-contain filter brightness-0 invert opacity-50 group-hover:opacity-0 transition-all duration-300 pointer-events-none mx-auto my-auto block"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-center opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-xs font-semibold uppercase tracking-wider text-white text-center">
                      {item.name}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Production Process (Süreç) with HERO-4 Frosted Glass */}
      <section className="py-16 sm:py-20 relative z-10 overflow-hidden border-b border-white/[0.08]">
        {/* Frosted Glass Background Image (High Visibility) */}
        <div className="absolute inset-0 z-0">
          <img
            src={hero4}
            alt="WE24 Agency Workflow & Studio Process"
            className="w-full h-full object-cover object-center filter brightness-[0.78] contrast-105 scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#071927]/60 via-black/45 to-[#071927]/65 backdrop-blur-[3px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-[#d5503b] font-semibold mb-1.5 block">
              {language === 'TR' ? "Süreç" : "Workflow"}
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display tracking-tight text-white drop-shadow-md">
              {t.processTitle}
            </h2>
            <p className="text-xs sm:text-sm text-zinc-200 max-w-md mx-auto mt-1.5 leading-relaxed">
              {t.processSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 sm:gap-5">
            {processSteps.map((step) => (
              <div
                key={step.num}
                className="bg-black/45 border border-white/20 p-5 rounded-2xl flex flex-col gap-3 relative backdrop-blur-xl hover:border-[#d5503b]/60 hover:bg-black/55 transition-all shadow-2xl"
              >
                <span className="text-2xl sm:text-3xl font-display font-bold text-[#d5503b] drop-shadow-sm">{step.num}</span>
                <h3 className="text-sm sm:text-base font-bold text-white leading-snug">{step.title}</h3>
                <p className="text-xs text-zinc-200 leading-relaxed font-normal">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Studio Team Callout (with Parallax Stars Background) */}
      <section className="py-20 sm:py-24 bg-[#030811] relative z-10 overflow-hidden border-b border-white/[0.08]">
        {/* Parallax Stars Background */}
        <ParallaxStars density="high" speed={0.75} opacity={0.8} />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-[#d5503b] font-bold mb-2 block">
                {language === 'TR' ? "Stüdyo ve Ekip" : "Studio & Collective"}
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-display tracking-tight text-white mb-4 drop-shadow-[0_4px_16px_rgba(0,0,0,0.8)]">
                {language === 'TR'
                  ? "Kolektif zekâ, kesintisiz üretim gücü."
                  : "Collective intelligence, relentless production power."}
              </h2>
              <p className="text-xs sm:text-sm text-zinc-300 leading-relaxed mb-6 font-normal">
                {language === 'TR'
                  ? "Şişli'deki stüdyomuzda yönetmenler, görüntü yönetmenleri, kurgucular, renk uzmanları, tasarımcılar ve yazılımcılar tek bir masa etrafında çalışır. Aracısız iletişim, hızlı karar alma ve doğrudan prodüksiyon yeteneğiyle projenizi en verimli şekilde hayata geçiriyoruz."
                  : "In our Istanbul studio, directors, cinematographers, editors, colorists, designers, and engineers collaborate seamlessly. Direct communication, rapid decision-making, and pure production mastery bring your project to fruition."}
              </p>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3 bg-[#071927]/70 border border-white/15 p-4 rounded-2xl backdrop-blur-xl shadow-xl hover:border-[#d5503b]/60 transition-all">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                  <div className="text-xs sm:text-sm">
                    <span className="font-semibold text-white">Larissa Davulciyan</span> <span className="text-zinc-400">- Agency Founder & Producer</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#071927]/70 border border-white/15 p-4 rounded-2xl backdrop-blur-xl shadow-xl hover:border-[#d5503b]/60 transition-all">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                  <div className="text-xs sm:text-sm">
                    <span className="font-semibold text-white">Harutyun Arto Davulciyan</span> <span className="text-zinc-400">- Creative Director & Cinematographer</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#071927]/70 border border-white/15 p-4 rounded-2xl backdrop-blur-xl shadow-xl hover:border-[#d5503b]/60 transition-all">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
                  <div className="text-xs sm:text-sm">
                    <span className="font-semibold text-white">Aren Arda Kaya</span> <span className="text-zinc-400">- Art Director</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Visual presentation */}
            <div className="relative aspect-video lg:aspect-square bg-[#071927]/60 rounded-3xl overflow-hidden border border-white/20 backdrop-blur-2xl flex flex-col justify-center items-center p-8 shadow-2xl hover:border-white/30 transition-all">
              <Users className="w-14 h-14 text-[#d5503b] mb-4 drop-shadow-[0_0_12px_rgba(213,80,59,0.4)]" />
              <span className="text-sm font-semibold tracking-wider uppercase text-center text-white mb-2">
                {language === 'TR' ? "Çekirdek Ekip + Geniş Üretim Ağı" : "Core Team + Extended Production Network"}
              </span>
              <p className="text-xs text-zinc-300 text-center max-w-xs leading-relaxed font-normal">
                {language === 'TR'
                  ? "Her projeye özel kurulan set ekipleri, yazılımcı kadroları ve kreatif iş birlikleriyle her bütçede ve ölçekte en doğru çıktıyı sağlıyoruz."
                  : "Tailoring dedicated set crews, software teams, and creative specialists to deliver benchmark results for any project scope."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. Final CTA with HERO-1 Frosted Glass */}
      <section className="py-20 sm:py-24 relative z-10 text-center overflow-hidden border-t border-white/[0.08]">
        {/* Frosted Glass Background Image (High Visibility) */}
        <div className="absolute inset-0 z-0">
          <img
            src={hero1}
            alt="WE24 Agency Start Project"
            className="w-full h-full object-cover object-center filter brightness-[0.78] contrast-105 scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#071927]/60 via-black/45 to-[#071927]/65 backdrop-blur-[3px]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 flex flex-col items-center">
          <div className="bg-black/45 border border-white/20 p-8 sm:p-12 rounded-3xl backdrop-blur-2xl max-w-3xl shadow-2xl flex flex-col items-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display leading-tight tracking-tight text-white mb-4 drop-shadow-[0_4px_16px_rgba(0,0,0,0.8)]">
              {t.ctaTitle}
            </h2>
            <p className="text-xs sm:text-sm text-zinc-200 max-w-xl leading-relaxed mb-8 font-normal">
              {t.ctaSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3.5 w-full sm:w-auto">
              <Link
                to={language === 'TR' ? "/iletisim" : "/en/contact"}
                className="px-7 py-3 bg-foreground text-background text-xs sm:text-sm font-semibold rounded-full hover:bg-foreground/90 transition-all duration-300 w-full sm:w-auto text-center shadow-lg"
              >
                {t.ctaBtn}
              </Link>
              <a
                href="https://wa.me/905452292424"
                target="_blank"
                rel="noopener noreferrer"
                className="liquid-glass border border-white/25 bg-black/50 px-7 py-3 text-xs sm:text-sm font-semibold rounded-full hover:border-emerald-500/50 text-foreground transition-all duration-300 w-full sm:w-auto text-center inline-flex items-center justify-center gap-2 group shadow-xl"
              >
                <img src={whatsappIcon} alt="WhatsApp" className="w-4 h-4 object-contain group-hover:scale-110 transition-transform" />
                <span>{language === 'TR' ? "WhatsApp'tan Yazın" : "Chat on WhatsApp"}</span>
                <ArrowUpRight className="w-4 h-4 text-emerald-400" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
